import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector, useStore } from 'react-redux';

import SVG from 'components/base/SVG';
import PropertyDetailPopup from 'app/Property/Detail/Detail';

import { getMapCardPhoto, showMapCardNoPhotoOnError } from '../propertyPhoto';
import BedIcon from '../../../assets/images/Bed.svg';
import BathIcon from '../../../assets/images/Bath.svg';
import SqFtIcon from '../../../assets/images/SqFt.svg';
import LotIcon from '../../../assets/images/LotSize.svg';
import {
  getInvestorTypeColor,
  getInvestorTypeIcon,
  getInvestorTypeLabel,
  getInvestorTypeBackground,
} from '../investorTypeTheme';
import SaveInvestorButton from '../SaveInvestorButton';
import useSaveInvestor from '../useSaveInvestor';
import useSkipTraceInvestors from '../useSkipTraceInvestors';
import useDialInvestorPhone from '../useDialInvestorPhone';
import InvestorProfile from '../Profile';
import {
  buildPopoverModel,
  fetchSubjectPropertyInvestor,
  formatPhone,
  searchInvestors,
  selectSubject,
  selectSubjectComplete,
  selectInvestorFilters,
  selectLoading,
  selectInvestorContactOverrides,
} from 'data/investors';
import css from './style.scss';

// Hover card for a map pin — Figma "map card" (233:8892).
//
// Shows the pin's investor plus the property the pin sits on: classification
// tags, name, contact runs, the property's last sale and address, its
// bed/bath/sqft/lot row, and the investor's owned-property count.
//
// Reuses the property-info icons the existing search map card already uses
// (assets/images/Bed|Bath|SqFt|LotSize.svg — the design's own "Icons/Property
// Info Icons"), rather than duplicating them into the sprite; phone, mail and
// close come from the sprite.
//
// The photo band is the design's Photo Area (580:36831) at its clipped 125px,
// filled from the comparable's own `imageUrl`. Its gallery is not reproduced —
// the service sends one photo per property, and the design's nav arrows and
// dot bar are both `opacity: 0` in this state anyway. Nor is its white
// gradient: the design fades to white over 231px but clips the band at 125,
// so within the visible strip the overlay never gets past ~20% white, which is
// why the render shows a crisp edge.
//
// The camera and bookmark controls are still omitted — the bookmark is
// Add-to-Investor-List (S10), which does not exist yet. The design's LOT chip is
// now shown: the search response carries lot area in `lotSquareFeet`.
// Guards on any non-finite value, not just null: a field the service stops
// returning would otherwise reach toLocaleString as undefined and throw.
const Detail = ({ icon, alt, value, unit }) =>
  !Number.isFinite(Number(value)) ? null : (
    <React.Fragment>
      <span className={css.detail}>
        <img src={icon} alt={alt} className={css.detail__icon} />
        <span className={css.detail__value}>{value.toLocaleString('en-US')}</span>
        {!unit ? null : <span className={css.detail__unit}>{unit}</span>}
      </span>
      <span className={css.detail__divider} />
    </React.Fragment>
  );

const InvestorInfoPopover = ({ pin, onClose }) => {
  // ATAI-554: contacts edited in the Investor Profile (opened from this card's
  // Details or from the results card) are layered over the pin's search data.
  // Safe as a subscription despite the note below: this slice value only
  // changes on such an edit, never with search-state churn like `loading`.
  const contactOverrides = useSelector(selectInvestorContactOverrides);
  const pinInvestor = (pin && pin.investor) || {};
  const investor = Object.prototype.hasOwnProperty.call(contactOverrides, pinInvestor.id)
    ? { ...pinInvestor, contacts: contactOverrides[pinInvestor.id] }
    : pinInvestor;
  const model = buildPopoverModel(pin && pin.investor !== investor ? { ...pin, investor } : pin);
  const property = (pin && pin.property) || {};
  const { saved, toggleSave } = useSaveInvestor(investor);
  const { skipTrace, materializing } = useSkipTraceInvestors();
  const dispatch = useDispatch();
  // Grab the store to read on demand rather than useSelector: this popover is a
  // child of the OverlayView subtree, and a store *subscription* here re-renders
  // that subtree on every unrelated search-state change (e.g. `loading`
  // toggling). react-google-maps' OverlayView schedules its draw() through a
  // lodash `delay` in componentDidUpdate but never cancels it on unmount, so an
  // overlay that updates and then unmounts in the same churn throws
  // "Cannot read properties of undefined (reading 'overlayMouseTarget')". Not
  // subscribing keeps the map overlays off that path. Same reason InvestorMarker
  // itself grabs the store with useStore rather than connecting.
  const store = useStore();
  // Click-to-dial on the card's phone numbers, same as Results/InvestorCard
  // (see ../useDialInvestorPhone). Its two selectors only change with the
  // profile / click-to-dial load, not with search state, so they stay clear of
  // the re-render churn above. Undefined when dialing isn't available, in which
  // case the numbers keep their plain `tel:` links.
  const dialPhone = useDialInvestorPhone(investor);

  // Same "was this investor skip traced" signal the InvestorCard and the
  // Profile's ContactInfo use — the vendor either returned inline contacts or
  // it didn't. When it didn't, the design (Figma map card 580:38679) shows the
  // Skip Trace banner in place of the phone/email rows.
  const contacts = investor.contacts || [];
  const skipTraced = Boolean(contacts.length);

  // Refresh after a Skip Trace the same way Results/InvestorCard's `onSkipTraced`
  // does: re-run the live search rather than re-fetch a per-investor profile
  // (that endpoint 404s for a plain, never-saved search result — see the note in
  // Results/index.js). The map pins are built from the same `selectInvestorResults`
  // slice, so re-running the search re-feeds this pin its contacts.
  //
  // Dismiss this popover FIRST, then dispatch the search on the next tick. The
  // re-run refits the map (Map/index.js's pins effect), and letting the open
  // overlay tear down before that churn — rather than in the same commit — keeps
  // it clear of the OverlayView draw-after-unmount crash noted above. State is
  // read from the store at call time, not via a subscription, for the same
  // reason. `dispatch`/`store` stay valid after unmount (both are stable).
  //
  // The subject's own pin is fed by the subject property lookup, not the search,
  // and that lookup skips a repeat for the same address — so it is forced here
  // to pick up the owner's freshly traced contacts.
  const refreshResults = () => {
    if (onClose) onClose();
    setTimeout(() => {
      const state = store.getState();
      if (!selectSubjectComplete(state) || selectLoading(state)) return;
      const subject = selectSubject(state);
      if (pin && pin.isSubject) dispatch(fetchSubjectPropertyInvestor(subject, { force: true }));
      dispatch(searchInvestors(subject, selectInvestorFilters(state)));
    }, 0);
  };

  // Details opens the Investor Profile modal — the same PopupHolder modal the
  // InvestorCard's "View Details" opens, via the same `.open({ investor })`
  // action, so the two entry points land on an identical profile. Dispatch
  // reaches the real PopupHolder through the store re-Provider'd in
  // InvestorMarker (see the boundary note there). The pin's own popover is
  // dismissed so it isn't left floating on the map behind the modal.
  const openInvestorDetails = () => {
    dispatch(InvestorProfile.open({ investor }));
    if (onClose) onClose();
  };

  // Open Property Details the same way the InvestorCard's Owned Properties block
  // does (Results/OwnedProperties) — a stacked popup with `preventRouting`, not
  // a route push, so the map and its search results stay put underneath rather
  // than being navigated away. Keyed on PropStream's own `propertyId`, not the
  // vendor `batchDataPropertyId` Property Details can't look up. Dispatch reaches
  // the real PopupHolder because this subtree is re-Provider'd with the app store
  // in InvestorMarker (see the boundary note there).
  const openPropertyDetails = () =>
    dispatch(
      PropertyDetailPopup.open({
        propertyId: property.propertyId,
        preventRouting: true,
        match: { params: { id: property.propertyId } },
      })
    );

  return (
    <div className={css.card}>
      <img
        className={css.photo}
        src={getMapCardPhoto(model)}
        onError={showMapCardNoPhotoOnError}
        alt=""
      />

      {/* Floated over the photo band, as the design places it (top 16, right
          16) rather than on a row of its own. */}
      <div className={css.header}>
        <button type="button" className={css.ghostButton} onClick={openInvestorDetails}>
          Details
        </button>
        {!onClose ? null : (
          <button type="button" className={css.close} onClick={onClose} aria-label="Close">
            <SVG icon="iconInvestorClose" className={css.close__icon} />
          </button>
        )}
      </div>

      <div className={css.info}>
        <div className={css.static}>
          <div className={css.tags}>
            {model.classifications.map((type) => {
              const color = getInvestorTypeColor(type);
              const background = getInvestorTypeBackground(type);
              const icon = getInvestorTypeIcon(type);

              return (
                <span key={type} className={css.tag} style={{ color, backgroundColor: background }}>
                  {!icon ? null : <SVG icon={icon} className={css.tag__glyph} />}
                  {getInvestorTypeLabel(type)}
                </span>
              );
            })}
            {!model.entityType ? null : <span className={css.tagEntity}>{model.entityType}</span>}
          </div>

          <div className={css.mainData}>
            {/* Save sits at the right of the name, in the white content area
                rather than over the photo (ATAI-451). The click is stopped here
                so it can't fall through to the pin or the map. */}
            <div className={css.nameRow}>
              <p className={css.name} title={model.name}>
                {model.name}
              </p>
              <SaveInvestorButton
                saved={saved}
                onToggle={(e) => {
                  e.stopPropagation();
                  toggleSave();
                }}
                className={css.saveButton}
                iconClassName={css.saveButton__icon}
                // Prototype labels: "Save" while unsaved, "View" once saved.
                label={saved ? 'View' : 'Save'}
                labelClassName={css.saveButton__label}
                hoverIconClassName={css.saveButton__hoverIcon}
              />
            </div>

            {/* Not skip traced -> the Figma map card's Skip Trace banner
                (580:37843) in place of the contact rows. Wired exactly like the
                InvestorCard's own button: a single-investor Skip Trace through
                the shared useSkipTraceInvestors, refreshing the search on
                success so the traced contacts land back on this pin. No
                onModalToggle -- unlike the Profile's ContactInfo, this popover
                is a map overlay, not a child of a SimpleModal whose outside-click
                the stacked modal would trip. */}
            {!skipTraced ? (
              <div className={css.skipTrace}>
                <p className={css.skipTrace__copy}>
                  Access the contact information for this investor at no cost.
                </p>
                <button
                  type="button"
                  className={css.skipTrace__button}
                  disabled={materializing}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    skipTrace([investor], { onSuccess: refreshResults });
                  }}
                >
                  <SVG icon="iconInvestorSkipTrace" className={css.skipTrace__buttonIcon} />
                  Skip Trace
                </button>
              </div>
            ) : !model.phones.length && !model.emails.length ? null : (
              <div className={css.contacts}>
                {!model.phones.length ? null : (
                  <p className={css.contact}>
                    <span className={css.contact__iconBox}>
                      <SVG icon="cp-call" className={css.contact__phoneIcon} />
                    </span>
                    <span className={css.contact__values}>
                      {model.phones.map((phone, i) => (
                        <React.Fragment key={phone}>
                          {i === 0 ? null : ', '}
                          <a
                            href={`tel:${phone}`}
                            className={css.contact__link}
                            onClick={
                              !dialPhone
                                ? undefined
                                : (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    dialPhone({ number: phone });
                                  }
                            }
                          >
                            {formatPhone(phone)}
                          </a>
                        </React.Fragment>
                      ))}
                    </span>
                  </p>
                )}

                {!model.emails.length ? null : (
                  <p className={css.contact}>
                    <span className={css.contact__iconBox}>
                      <SVG icon="cp-mail" className={css.contact__mailIcon} />
                    </span>
                    <span className={css.contact__values}>{model.emails.join(', ')}</span>
                  </p>
                )}
              </div>
            )}

            <div className={css.divider} />

            <div className={css.sale}>
              {/* Either half can be missing on its own — some comparables carry
                  a sale date with no price — so the row shows whatever exists. */}
              {!model.price && !model.purchasedOn ? null : (
                <p className={css.sale__price}>
                  {model.price}
                  {!model.purchasedOn ? null : (
                    <span className={css.sale__date}>{model.purchasedOn}</span>
                  )}
                </p>
              )}
              {!model.address ? null : property.propertyId ? (
                // Rendered as a button, not a link/route: same reasoning as
                // Owned Properties — keep the search underneath and open Details
                // as its own popup. The design already styles the address teal,
                // so it reads as actionable.
                <button
                  type="button"
                  className={classNames(css.sale__address, css.sale__addressButton)}
                  onClick={openPropertyDetails}
                  title={model.address}
                >
                  {model.address}
                </button>
              ) : (
                <p className={css.sale__address}>{model.address}</p>
              )}
            </div>

            <div className={css.details}>
              <Detail icon={BedIcon} alt="Bedrooms" value={model.beds} />
              <Detail icon={BathIcon} alt="Bathrooms" value={model.baths} />
              <Detail icon={SqFtIcon} alt="Living area" value={model.squareFeet} unit="SQFT" />
              {/* LOT is the lot area in square feet (`lotSquareFeet`), but the
                  design labels the chip "LOT", not "SQFT" — Figma 233:8886. */}
              <Detail icon={LotIcon} alt="Lot size" value={model.lot} unit="LOT" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

InvestorInfoPopover.propTypes = {
  pin: PropTypes.object.isRequired,
  onClose: PropTypes.func,
};

export default InvestorInfoPopover;
